import { DateTime } from 'luxon';
import type { PartialDeep } from 'type-fest';
import * as Yup from 'yup';

import Enums from '@core/types/api/Enums';
import {
  ReminderUserSetting,
  type ReminderUserSettingWireFormat,
} from '@core/types/api/ReminderUserSetting/ReminderUserSetting';
import type { ReminderUserSettingsRecordDataWireFormat } from '@core/types/api/ReminderUserSettingsRecordData/ReminderUserSettingsRecordData';
import type { EntityType } from '@core/types/search';
import { ParseServerDuration } from '@core/utils/dateHelpers';
import { hasStringValue } from '@core/utils/equality';
import {
  ReminderBusinessDatesByRecordType,
  ReminderRecordTypeByEntityType,
  reminderRecordSummary,
} from '@core/utils/reminderRecordTypes';
import { DEFAULT_NOTIFY_BEFORE } from '@core/utils/reminders';
import { NIL_UUID } from '@core/utils/uuid';
import {
  TestForUndefined,
  YupEnum,
  YupNullableDuration,
  YupNullableString,
  YupString,
} from '@core/utils/yupHelpers';

export type UserReminderFormikType = Omit<
  ReminderUserSettingWireFormat,
  | 'recordType'
  | 'recordData'
  | 'recordBusinessDate'
  | 'createdAt'
  | 'updatedAt'
  | 'isArchived'
  | 'notifyBefore'
> & {
  recordType: Enums.ReminderUserSettingsRecordType;
  recordId: string;
  recordBusinessDate: Enums.ReminderUserSettingsBusinessDate;
  notifyBefore: string | null;
};

type UserReminderRecordChange = Pick<UserReminderFormikType, 'recordType'> &
  Partial<Pick<UserReminderFormikType, 'recordBusinessDate' | 'notifyAt'>>;

export const userReminderRecordChange = (
  businessDate: Enums.ReminderUserSettingsBusinessDate,
  entityType: EntityType,
): UserReminderRecordChange => {
  const recordType =
    ReminderRecordTypeByEntityType[entityType] ?? Enums.ReminderUserSettingsRecordType.Project;

  if (businessDate === Enums.ReminderUserSettingsBusinessDate.General) {
    return { recordType };
  }

  return {
    recordType,
    recordBusinessDate: ReminderBusinessDatesByRecordType[recordType][0],
    notifyAt: null,
  };
};

export const NOTIFY_AT_REQUIRED_ERROR = 'Fire date and time is required';

export const UserReminderSchemaFormik = (): Yup.SchemaOf<Omit<UserReminderFormikType, 'id'>> =>
  Yup.object()
    .shape({
      recordType: YupEnum(Enums.ReminderUserSettingsRecordType, 'Record Type'),
      recordId: YupString('Record'),
      recordBusinessDate: YupEnum(Enums.ReminderUserSettingsBusinessDate, 'Business Date').when(
        'recordType',
        (recordType: Enums.ReminderUserSettingsRecordType, schema: Yup.AnySchema) =>
          schema.test(
            'business-date-matches-record-type',
            'Business Date is not valid for this Record Type',
            (businessDate: Enums.ReminderUserSettingsBusinessDate) =>
              (ReminderBusinessDatesByRecordType[recordType] ?? []).includes(businessDate),
          ),
      ),
      notifyBefore: YupNullableDuration('Reminder'),
      notifyAt: YupNullableString('Notify At').when('recordBusinessDate', {
        is: Enums.ReminderUserSettingsBusinessDate.General,
        then: (schema) =>
          schema.test('notify-at-required', NOTIFY_AT_REQUIRED_ERROR, (notifyAt) =>
            hasStringValue(notifyAt),
          ),
        otherwise: (schema) => schema,
      }),
      note: YupNullableString('Note'),
    })
    .test(TestForUndefined('UserReminderSchemaFormik'));

export const FormikUserReminder = (
  reminderData?: Partial<ReminderUserSetting>,
): UserReminderFormikType => {
  const recordType = reminderData?.recordType ?? Enums.ReminderUserSettingsRecordType.Project;
  const recordData = reminderData?.recordData;

  const recordId = reminderRecordSummary(recordType, recordData)?.id ?? '';

  return {
    id: reminderData?.id ?? NIL_UUID,
    recordType,
    recordId,
    recordBusinessDate:
      reminderData?.recordBusinessDate ?? ReminderBusinessDatesByRecordType[recordType][0],
    notifyBefore: reminderData?.notifyBefore?.toISO() ?? DEFAULT_NOTIFY_BEFORE,
    // notify_at is a tenant-local wall clock stored as UTC; feed the picker those
    // digits, not the browser-shifted instant.
    notifyAt: reminderData?.notifyAt?.toUTC().toFormat("yyyy-MM-dd'T'HH:mm") ?? null,
    note: reminderData?.note ?? null,
  };
};

const userReminderRecordData = (
  values: UserReminderFormikType,
): PartialDeep<ReminderUserSettingsRecordDataWireFormat> => {
  switch (values.recordType) {
    case Enums.ReminderUserSettingsRecordType.Project:
      return { project: { id: values.recordId } };
    case Enums.ReminderUserSettingsRecordType.Quote:
      return { quote: { id: values.recordId } };
    case Enums.ReminderUserSettingsRecordType.Activity:
      return { activity: { id: values.recordId } };
    default:
      return {};
  }
};

export const formikUserReminderToWire = (values: UserReminderFormikType): ReminderUserSetting => {
  const isBusinessDateGeneral =
    values.recordBusinessDate === Enums.ReminderUserSettingsBusinessDate.General;

  return new ReminderUserSetting({
    id: values.id ?? NIL_UUID,
    recordType: values.recordType,
    recordData: userReminderRecordData(values),
    recordBusinessDate: values.recordBusinessDate,
    notifyBefore:
      !isBusinessDateGeneral && hasStringValue(values.notifyBefore)
        ? ParseServerDuration(values.notifyBefore)
        : null,
    notifyAt:
      isBusinessDateGeneral && hasStringValue(values.notifyAt)
        ? // The picker's wall clock is tenant-local; keep the digits verbatim (tag as
          // UTC) so v_personal_reminder_triggers re-anchors them to the tenant zone.
          DateTime.fromISO(values.notifyAt, { zone: 'utc' })
        : null,
    note: hasStringValue(values.note) ? values.note : null,
  });
};
